'use client';

import { useState, useCallback, useRef } from 'react';

import { useToast } from './useToast';

export interface UseCopyToClipboardReturn {
  /** 현재 복사된 필드 ID (2초 후 자동 리셋) */
  copiedId: string | null;
  /** 텍스트를 클립보드에 복사 */
  copy: (text: string, label?: string, id?: string) => Promise<boolean>;
  /** 특정 ID가 현재 복사 상태인지 확인 */
  isCopied: (id: string) => boolean;
}

/**
 * 클립보드 복사 기능을 제공하는 훅
 *
 * @example
 * const { copy, isCopied } = useCopyToClipboard();
 *
 * <button onClick={() => copy(text, '제목', 'title')}>
 *   {isCopied('title') ? '복사됨' : '복사'}
 * </button>
 */
export function useCopyToClipboard(): UseCopyToClipboardReturn {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { success: toastSuccess } = useToast();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(
    async (text: string, label?: string, id?: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // 클립보드 API 실패 시 fallback
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      // 타이머 정리 후 새로 설정
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      setCopiedId(id ?? '_default');
      toastSuccess(label ? `${label} 복사됨` : '복사되었습니다');
      timerRef.current = setTimeout(() => setCopiedId(null), 2000);

      return true;
    },
    [toastSuccess]
  );

  const isCopied = useCallback(
    (id: string): boolean => copiedId === id,
    [copiedId]
  );

  return { copiedId, copy, isCopied };
}
